import { apiRequest } from '../utils/apiClient';

// Phase 6 — the Project hub (developer projects + land schemes مخطط). A Project
// is owned by an organization (Company with isDeveloper/isInvestor) and groups
// Property units. Backend: /api/projects/* (the apiClient base already adds /api).
// The Land-Scheme `kind` UI is gated by VITE_LAND_SCHEMES_ENABLED on the frontend.

export type ProjectKind = 'PROJECT' | 'LAND_SCHEME';
export type ProjectStatus = 'PLANNING' | 'UNDER_CONSTRUCTION' | 'READY' | 'SOLD_OUT';

export interface MyProject {
  id: string;
  slug: string;
  name: string;
  nameAr?: string | null;
  city?: string | null;
  region?: string | null;
  status?: ProjectStatus | null;
  kind?: ProjectKind | null;
  coverUrl?: string | null;
  currency?: string | null;
  totalUnits?: number | null;
  landArea?: number | null;
  unitsCount?: number;
  investorsCount?: number;
  createdAt?: string;
  // Phase 9 off-plan eligibility (owner-only; only present on /projects/my).
  wafiApproved?: boolean | null;
  offPlanSalesEnabled?: boolean | null;
  escrow?: { status?: EscrowStatus | null } | null;
}

export type EscrowStatus = 'PENDING' | 'ACTIVE' | 'FUNDED' | 'RELEASED' | 'CLOSED';

export interface OffplanState {
  id: string;
  wafiApproved: boolean;
  offPlanSalesEnabled: boolean;
  escrow?: { status?: EscrowStatus | null } | null;
}

export interface SetOffplanInput {
  wafiApproved?: boolean;
  offPlanSalesEnabled?: boolean;
  escrowStatus?: EscrowStatus;
}

export interface MyProjectsResponse {
  data: MyProject[];
  myRole?: string;
  capabilities?: { isBrokerage?: boolean; isDeveloper?: boolean; isInvestor?: boolean };
}

export interface CreateProjectInput {
  name: string;
  nameAr?: string;
  description?: string;
  descriptionAr?: string;
  city?: string;
  region?: string;
  budgetCost?: number;
  currency?: string;
  landArea?: number;
  totalUnits?: number;
  status?: ProjectStatus;
  startDate?: string;
  endDate?: string;
  kind?: ProjectKind;
  masterPlotGeometry?: string;
}

// ── Public project list (Bucket B — investment hub) ────────────────────────
export type ProjectInvestmentStatus = 'OPEN_FOR_INVESTMENT' | 'CLOSED' | 'NA';

export interface ProjectInvestor {
  id: string;
  ownerType: 'company' | 'individual';
  name: string | null;
  nameAr?: string | null;
  logoUrl?: string | null;
  slug?: string | null;
  amount?: string | number | null;
  sharePercent?: number | null;
  status?: string;
}

export interface PublicProject {
  id: string;
  slug: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  coverUrl?: string | null;
  city?: string | null;
  region?: string | null;
  status?: ProjectStatus | null;
  investmentStatus?: ProjectInvestmentStatus | null;
  kind?: ProjectKind | null;
  totalUnits?: number | null;
  unitsCount?: number;
  investorsCount?: number;
  budgetCost?: string | number | null;
  currency?: string | null;
  contributionMin?: string | number | null;
  contributionMax?: string | number | null;
  seekingContribution?: boolean;
  currentInvestors?: ProjectInvestor[];
  developer?: { id: string; name: string; nameAr?: string | null; slug: string; logoUrl?: string | null; isVerified?: boolean } | null;
}

export interface PublicProjectsResponse {
  data: PublicProject[];
  meta: { page: number; pageSize: number; total: number; totalPages: number };
}

export async function fetchPublicProjects(params: {
  city?: string;
  areaId?: string;
  listingType?: string;
  seekingContribution?: boolean;
  investment?: boolean;
  page?: number;
  pageSize?: number;
} = {}): Promise<PublicProjectsResponse> {
  const q = new URLSearchParams();
  if (params.city) q.set('city', params.city);
  if (params.areaId) q.set('areaId', params.areaId);
  if (params.listingType) q.set('listingType', params.listingType);
  if (params.seekingContribution) q.set('seekingContribution', 'true');
  if (params.investment) q.set('investment', 'true');
  q.set('page', String(params.page ?? 1));
  q.set('pageSize', String(params.pageSize ?? 12));
  return apiRequest<PublicProjectsResponse>(`/projects?${q.toString()}`);
}

export async function listMyProjects(token: string): Promise<MyProjectsResponse> {
  return apiRequest<MyProjectsResponse>('/projects/my', { token });
}

export async function createProject(token: string, input: CreateProjectInput): Promise<MyProject> {
  const res = await apiRequest<{ data: MyProject }>('/projects', { method: 'POST', token, body: input });
  return res.data;
}

// Phase 9 — manage off-plan (بيع على الخارطة) eligibility: Wafi approval +
// off-plan toggle + escrow status. Backend gated by OFFPLAN_WAFI_ENABLED (404 off).
export async function setProjectOffplan(token: string, id: string, input: SetOffplanInput): Promise<OffplanState> {
  const res = await apiRequest<{ data: OffplanState }>(`/projects/${id}/offplan`, { method: 'PATCH', token, body: input });
  return res.data;
}
